

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaFilePdf, FaDownload, FaPaperPlane, FaSignature } from "react-icons/fa";
import api from '../api/axios';
import Navbar from "./Navbar";

const COLORS = { blue: "#2563EB", orange: "#F59E0B", dark: "#0D1B3D" };

const STATUS_STYLES = {
  draft: { bg: "#F3F4F6", text: "#6B7280" },
  sent: { bg: "#EFF6FF", text: COLORS.blue },
  viewed: { bg: "#F5F3FF", text: "#7C3AED" },
  accepted: { bg: "#F0FDF4", text: "#16A34A" },
  negotiation: { bg: "#FFFBEB", text: COLORS.orange },
  rejected: { bg: "#FEF2F2", text: "#DC2626" },
  expired: { bg: "#F3F4F6", text: "#9CA3AF" },
};

export default function EmployerOfferDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadOffer = () => {
    setLoading(true);
    api
      .get(`/offers/${id}`)
      .then((res) => setOffer(res.data))
      .catch(() => setError("Couldn't load this offer."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    loadOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const generatePdf = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/offers/${id}/generate-pdf`);
      loadOffer();
    } catch {
      setError("Couldn't generate the offer letter.");
    } finally {
      setBusy(false);
    }
  };

  // Fetch through the authenticated axios client as a blob, then trigger the
  // download client-side. A plain window.open()/<a href> straight to the API
  // URL doesn't carry the axios Authorization header, so it hits the
  // auth:sanctum-protected route unauthenticated and returns 401 JSON instead
  // of the file.
  const downloadPdf = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await api.get(`/offers/${id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `offer-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Couldn't download the offer letter.");
    } finally {
      setBusy(false);
    }
  };

  const sendOffer = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/offers/${id}/send`);
      loadOffer();
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't send the offer.");
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
  const formatSalary = (v) => "₹" + Number(v).toLocaleString("en-IN");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EFF6FF]">
        <Navbar user={user} />
        <div className="py-24 text-center text-gray-400">Loading offer...</div>
      </div>
    );
  }

  if (error && !offer) {
    return (
      <div className="min-h-screen bg-[#EFF6FF]">
        <Navbar user={user} />
        <div className="mx-auto max-w-3xl px-4 py-12">
          <button
            onClick={() => navigate("/employer/offers")}
            className="mb-4 flex items-center gap-1 text-sm font-medium"
            style={{ color: COLORS.blue }}
          >
            <FaArrowLeft /> Back to Offers
          </button>
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  const style = STATUS_STYLES[offer.status] || STATUS_STYLES.draft;

  return (
    <div className="min-h-screen bg-[#EFF6FF]">
      <Navbar user={user} />

      <div className="mx-auto max-w-3xl px-4 py-8">
        <button
          onClick={() => navigate("/employer/offers")}
          className="mb-4 flex items-center gap-1 text-sm font-medium"
          style={{ color: COLORS.blue }}
        >
          <FaArrowLeft /> Back to Offers
        </button>

        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: COLORS.dark }}>
              {offer.candidate_name}
            </h1>
            <p className="text-sm text-gray-500">
              {formatSalary(offer.salary)} · Joining {formatDate(offer.joining_date)}
            </p>
          </div>
          <span
            className="rounded-full px-3 py-1 text-xs font-medium capitalize"
            style={{ backgroundColor: style.bg, color: style.text }}
          >
            {offer.status}
          </span>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
        )}

        <h2 className="mb-3 text-sm font-semibold uppercase text-gray-400">Documents</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
  <div className="mb-2 text-xs font-semibold uppercase text-gray-400">PDF Generate</div>
  {!offer.pdf_path ? (
    <button
      disabled={busy}
      onClick={generatePdf}
      className="flex items-center gap-2 text-sm font-medium disabled:opacity-50"
      style={{ color: COLORS.dark }}
    >
      <FaFilePdf /> {busy ? "Generating..." : "Generate"}
    </button>
  ) : (
    <div>
      <p className="text-sm text-gray-500 mb-2">Already generated</p>
      <button
        disabled={busy}
        onClick={generatePdf}
        className="flex items-center gap-2 text-xs font-medium disabled:opacity-50"
        style={{ color: COLORS.blue }}
      >
        <FaFilePdf /> {busy ? "Regenerating..." : "Regenerate PDF"}
      </button>
    </div>
  )}
</div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-2 text-xs font-semibold uppercase text-gray-400">Digital Signature</div>
            {offer.status === "accepted" && offer.signature_name ? (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <FaSignature style={{ color: COLORS.blue }} />
                <span className="italic">{offer.signature_name}</span>
              </div>
            ) : (
              <span className="text-sm text-gray-400">Not signed yet</span>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-2 text-xs font-semibold uppercase text-gray-400">Download</div>
            {offer.pdf_path ? (
              <button
                disabled={busy}
                onClick={downloadPdf}
                className="flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                style={{ color: COLORS.dark }}
              >
                <FaDownload /> {busy ? "Downloading..." : "Download"}
              </button>
            ) : (
              <span className="text-sm text-gray-400">Generate PDF first</span>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-2 text-xs font-semibold uppercase text-gray-400">Email Candidate</div>
            {offer.status === "draft" && offer.pdf_path ? (
              <button
                disabled={busy}
                onClick={sendOffer}
                className="flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                style={{ color: COLORS.blue }}
              >
                <FaPaperPlane /> {busy ? "Sending..." : "Email Candidate"}
              </button>
            ) : offer.status === "draft" ? (
              <span className="text-sm text-gray-400">Generate PDF first</span>
            ) : (
              <span className="text-sm text-gray-500">Already sent</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}